import { useState } from 'react';
import { useTableColumnSettings } from '@/hooks/useTableColumnSettings';
import {
  serverSortFromSorting,
  sortingFromServerSort,
  type ServerSort,
} from '@/components/common/data-table/server-sort';
import {
  ArTasksSortBySchema,
  type ArTaskRow,
  type ArTasksSortBy,
  type ListArTasksInput,
} from '@erp/rpc';
import {
  type ColumnDef,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Updater,
  appTableFeatures,
  useTable,
} from '@/lib/tanstack-table';

function resolvePagination(
  updater: Updater<PaginationState>,
  current: PaginationState,
): PaginationState {
  return typeof updater === 'function' ? updater(current) : updater;
}

function resolveSorting(
  updater: Updater<SortingState>,
  current: SortingState,
): SortingState {
  return typeof updater === 'function' ? updater(current) : updater;
}

/** A column id as a sort key; columns the list can't sort by drop out. */
function toSortBy(columnId: string): ArTasksSortBy | undefined {
  const parsed = ArTasksSortBySchema.safeParse(columnId);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Server-paged, server-sorted AR Tasks table with row selection. Selection
 * is per query: any filter, sort or page change clears it, so a bulk action
 * never reaches rows no longer shown.
 */
export function useArTasksTable({
  columns,
  rows,
  total,
  listInput,
  hiddenColumns,
  onPageChange,
  onSortChange,
}: {
  columns: ColumnDef<ArTaskRow>[];
  rows: ArTaskRow[];
  total: number;
  listInput: ListArTasksInput;
  /** Hidden on top of the saved layout (e.g. Assignee while grouped by it); not saved. */
  hiddenColumns?: Record<string, boolean>;
  onPageChange: (page: number, pageSize: number) => void;
  /** Every key present, so a cleared sort clears the URL. */
  onSortChange: (sort: ServerSort<ArTasksSortBy>) => void;
}) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectionKey = JSON.stringify(listInput);
  const [prevSelectionKey, setPrevSelectionKey] = useState(selectionKey);
  if (prevSelectionKey !== selectionKey) {
    setPrevSelectionKey(selectionKey);
    setRowSelection({});
  }

  const pagination: PaginationState = {
    pageIndex: listInput.page - 1,
    pageSize: listInput.pageSize,
  };
  const sorting: SortingState = sortingFromServerSort(listInput);

  // Per-user column layout (order, visibility, widths), as on Cash Receipts.
  const layout = useTableColumnSettings('ar-tasks');

  const table = useTable({
    features: appTableFeatures,
    columns,
    data: rows,
    manualPagination: true,
    manualSorting: true,
    // Shift-click a header to add it as the next sort level.
    enableMultiSort: true,
    rowCount: total,
    state: {
      pagination,
      sorting,
      rowSelection,
      columnOrder: layout.columnOrder,
      columnVisibility: { ...layout.columnVisibility, ...hiddenColumns },
      columnSizing: layout.columnSizing,
    },
    onColumnOrderChange: layout.onColumnOrderChange,
    onColumnVisibilityChange: layout.onColumnVisibilityChange,
    onColumnSizingChange: layout.onColumnSizingChange,
    getRowId: (row) => row.taskId,
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updater) => {
      const next = resolveSorting(updater, sorting);
      onSortChange(serverSortFromSorting(next, toSortBy));
    },
    onPaginationChange: (updater) => {
      const next = resolvePagination(updater, pagination);
      onPageChange(next.pageIndex + 1, next.pageSize);
    },
  });

  return {
    table,
    selectedRows: table.getSelectedRowModel().rows.map((r) => r.original),
    clearSelection: () => setRowSelection({}),
    resetLayout: layout.resetLayout,
  };
}
